import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Repeat, MapPin, Calendar, Gift, Loader2, Car } from "lucide-react";

const RIDE_LABELS = { course_simple: "Course simple", aller_retour: "Aller-retour", mise_a_disposition: "Mise à disposition", transfert_aeroport: "Transfert aéroport" };
const STATUS = { confirmee: ["Confirmée", "bg-blue-500/15 text-blue-300"], en_cours: ["En cours", "bg-amber-500/15 text-amber-300"], terminee: ["Terminée", "bg-emerald-500/15 text-emerald-300"], annulee: ["Annulée", "bg-red-500/15 text-red-300"] };

export default function ClientSpace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/client/bookings"), api.get("/client/loyalty")])
      .then(([b, l]) => { setBookings(b.data); setLoyalty(l.data); })
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, []);

  const rebook = (b) => {
    const q = new URLSearchParams({ pickup: b.pickup, dropoff: b.dropoff || "", ride_type: b.ride_type });
    navigate(`/?${q.toString()}`);
  };

  if (loading) return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#39FF14]" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0F17] grain">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-up">
        <h1 className="font-display text-3xl font-bold text-slate-50">Bonjour {user?.name?.split(" ")[0]}</h1>
        <p className="text-slate-400 mt-1">Retrouvez vos courses et vos avantages fidélité.</p>

        {/* Loyalty */}
        {loyalty && (
          <div className="card-surface rounded-2xl p-6 mt-6 gold-glow">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-[#39FF14]/15 flex items-center justify-center"><Gift className="h-5 w-5 text-[#39FF14]" /></div>
                <div>
                  <h2 className="font-display text-lg font-semibold text-slate-100">Programme fidélité</h2>
                  <p className="text-sm text-slate-400">1 course offerte tous les 10 trajets</p>
                </div>
              </div>
              {loyalty.free_rides_earned > 0 && (
                <Badge className="bg-[#39FF14] text-[#0B0F17] font-semibold">{loyalty.free_rides_earned} course{loyalty.free_rides_earned > 1 ? "s" : ""} offerte{loyalty.free_rides_earned > 1 ? "s" : ""} 🎉</Badge>
              )}
            </div>
            <div className="flex gap-2 mt-5" data-testid="client-loyalty-counter-display">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className={`flex-1 aspect-square rounded-full border flex items-center justify-center transition-all ${i < loyalty.progress ? "bg-[#39FF14] border-[#39FF14]" : "border-[#334155] bg-[#0B0F17]"}`}>
                  <Car className={`h-3.5 w-3.5 ${i < loyalty.progress ? "text-[#0B0F17]" : "text-slate-600"}`} />
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-400 mt-3">{loyalty.progress}/10 · plus que <span className="text-[#39FF14] font-semibold">{loyalty.rides_to_next}</span> course(s) avant votre prochaine offerte</p>
          </div>
        )}

        {/* Bookings */}
        <h2 className="font-display text-xl font-semibold text-slate-100 mt-10 mb-4">Mes courses ({bookings.length})</h2>
        <div className="space-y-3">
          {bookings.length === 0 && <p className="text-slate-500 text-sm">Aucune course pour le moment.</p>}
          {bookings.map((b) => {
            const st = STATUS[b.status] || STATUS.confirmee;
            return (
              <div key={b.id} className="card-surface rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-100">{RIDE_LABELS[b.ride_type]}</span>
                    <Badge className={`${st[1]} border-0 text-xs`}>{st[0]}</Badge>
                    <span className="font-mono text-xs text-slate-500">{b.reference}</span>
                  </div>
                  <p className="text-sm text-slate-300 mt-1.5 flex items-center gap-1.5 truncate"><MapPin className="h-3.5 w-3.5 text-[#39FF14] shrink-0" />{b.pickup}{b.dropoff ? ` → ${b.dropoff}` : ""}</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{b.date} à {b.time}</p>
                </div>
                <div className="flex items-center gap-4 sm:flex-col sm:items-end">
                  <span className="font-mono text-lg font-semibold text-[#39FF14]">{b.price?.toFixed(2)} €</span>
                  <Button onClick={() => rebook(b)} size="sm" data-testid="client-rebook-button"
                    className="bg-[#1C2436] hover:bg-[#243049] text-slate-100 border border-[#1E293B]">
                    <Repeat className="h-3.5 w-3.5 mr-1.5" />Reréserver
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
